<script setup>
import { ref, onMounted } from 'vue';
import { useNotificationStore } from '@/stores/NotificationStore.js';

const open = ref(false);
const webinars = ref([]);
const loading = ref(true);
const notificationStore = useNotificationStore();

const checkAuth = () => {
    try {
        axios.get(route('webinars.index')).then((response) => {
            webinars.value = response.data.data;
            loading.value = false;

            // Check if URL has ?live=open parameter to force open the webinar slideout
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get('live') === 'open') {
                open.value = true;
            } else {
                open.value = !!response.data.shouldOpen;
            }
        });
    } catch (e) {
        console.log(e);
    }
};

const timeUntilJoinable = (startTime) => {
    const start = new Date(startTime);
    const joinTime = new Date(start.getTime());
    const now = new Date();

    if (now >= joinTime) {
        return 'now';
    }

    const diff = joinTime.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
        return `in ${hours}h ${minutes}m`;
    } else {
        return `in ${minutes}m`;
    }
};

const joinWebinar = async (webinar) => {
    if (!webinar?.webinar_url) {
        return;
    }

    const popup = window.open('', '_blank');

    if (!popup) {
        notificationStore.addNotification({
            type: 'error',
            title: 'Unable to open webinar',
            message: 'Please allow pop-ups for myflow to join live events.',
        });

        return;
    }

    try {
        await axios.post(route('webinars.join'), {
            webinar_id: webinar.id,
        });

        popup.location = webinar.webinar_url;
    } catch (error) {
        popup.close();

        const status = error?.response?.status;

        notificationStore.addNotification({
            type: 'error',
            title: 'Unable to join webinar',
            message:
                status === 403
                    ? 'Please register for the webinar before joining.'
                    : 'Please try again or reload the page.',
        });

        console.error(error);
    }
};

const register = (webinarId) => {
    axios
        .post(route('webinars.register'), {
            webinar_id: webinarId,
        })
        .then((response) => {
            webinars.value.find(
                (webinar) => webinar.id === webinarId
            ).registered = true;
        })
        .catch((error) => {
            console.error(error);
        });
};

const viewDetails = (url) => {
    window.open(url, '_blank');
};

const formatCalendarDate = (dateOrString) => {
    const date =
        dateOrString instanceof Date
            ? dateOrString
            : new Date(dateOrString);

    if (Number.isNaN(date.getTime())) {
        return '';
    }

    return date
        .toISOString()
        .replace(/[-:]/g, '')
        .replace(/\.\d{3}/, '');
};

const cleanCalendarText = (value) => {
    if (!value) {
        return '';
    }

    return value
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
};

const getCalendarEvent = (webinar) => {
    if (!webinar?.utc_start_time || !webinar?.utc_end_time) {
        return null;
    }

    const start = new Date(webinar.utc_start_time);
    const end = new Date(webinar.utc_end_time);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return null;
    }

    return {
        title: webinar.title || 'Webinar',
        description: cleanCalendarText(webinar.description),
        location: webinar.webinar_url || webinar.page_url || '',
        start,
        end,
        id: webinar.id,
    };
};

const openGoogleCalendar = (webinar) => {
    const event = getCalendarEvent(webinar);

    if (!event) {
        return;
    }

    const params = new URLSearchParams({
        action: 'TEMPLATE',
        text: event.title,
        dates: `${formatCalendarDate(event.start)}/${formatCalendarDate(
            event.end
        )}`,
    });

    if (event.description) {
        params.append('details', event.description);
    }

    if (event.location) {
        params.append('location', event.location);
    }

    window.open(
        `https://calendar.google.com/calendar/render?${params.toString()}`,
        '_blank'
    );
};

const openOutlookCalendar = (webinar) => {
    const event = getCalendarEvent(webinar);

    if (!event) {
        return;
    }

    const description = event.location
        ? `${event.description ? `${event.description}\n\n` : ''}${event.location}`
        : event.description;

    const params = new URLSearchParams({
        path: '/calendar/action/compose',
        rru: 'addevent',
        subject: event.title,
        startdt: event.start.toISOString(),
        enddt: event.end.toISOString(),
    });

    if (description) {
        params.append('body', description);
    }

    if (event.location) {
        params.append('location', event.location);
    }

    window.open(
        `https://outlook.office.com/calendar/0/deeplink/compose?${params.toString()}`,
        '_blank'
    );
};

const escapeIcsValue = (value) =>
    value
        .replace(/\\/g, '\\\\')
        .replace(/\n/g, '\\n')
        .replace(/,/g, '\\,')
        .replace(/;/g, '\\;');

const slugifyForFilename = (value) => {
    if (!value) {
        return 'event';
    }

    return value
        .toString()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60) || 'event';
};

const downloadCalendarFile = (webinar) => {
    const event = getCalendarEvent(webinar);

    if (!event) {
        return;
    }

    const lines = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//myflow//mf webinar//EN',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        'BEGIN:VEVENT',
        `UID:${event.id}@myflow.app`,
        `DTSTAMP:${formatCalendarDate(new Date())}`,
        `DTSTART:${formatCalendarDate(event.start)}`,
        `DTEND:${formatCalendarDate(event.end)}`,
        `SUMMARY:${escapeIcsValue(event.title)}`,
    ];

    if (event.description) {
        lines.push(`DESCRIPTION:${escapeIcsValue(event.description)}`);
    }

    if (event.location) {
        lines.push(`LOCATION:${escapeIcsValue(event.location)}`);
        lines.push(`URL:${escapeIcsValue(event.location)}`);
    }

    lines.push('END:VEVENT', 'END:VCALENDAR');

    const blob = new Blob([lines.join('\r\n')], {
        type: 'text/calendar;charset=utf-8',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${slugifyForFilename(event.title)}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 0);
};

const onClose = () => {
    open.value = false;
};

onMounted(() => {
    checkAuth();
});
</script>
<template>
    <div>
        <button-base
            @click="open = true"
            :badge="webinars.filter((webinar) => !webinar.registered).length"
            :badgeColor="'bg-red-600'"
            >Next Up Live</button-base
        >

        <slideout-with-slots
            v-if="open"
            title="Next Up Live"
            @close="onClose"
            max-width-class="max-w-4xl"
        >
            <template #body>
                <div
                    class="py-4 -mx-4 -mt-6 text-sm italic text-center text-gray-800 bg-gray-100 border-b md:-mx-6"
                >
                    <p class="mb-0">
                        Live events are free to attend for you as a myflow user.
                    </p>
                </div>

                <div v-if="loading" class="py-8 text-center">
                    <i
                        class="block mb-3 text-3xl text-gray-300 fa-regular fa-calendar"
                    ></i>
                    <p class="text-gray-600">Loading upcoming events...</p>
                </div>
                <div
                    v-if="!loading && webinars.length === 0"
                    class="py-8 text-center"
                >
                    <i
                        class="block mb-3 text-3xl text-gray-300 fa-regular fa-calendar"
                    ></i>
                    <p class="text-gray-600">
                        There are no upcoming events to show here...
                    </p>
                </div>

                <list-basic v-else list-class="-mx-4 md:-mx-6">
                    <list-basic-item
                        v-for="webinar in webinars"
                        :key="webinar.id"
                    >
                        <div class="flex justify-between space-y-1">
                            <div>
                                <div class="flex flex-col space-y-1">
                                    <div
                                        class="flex flex-col w-full mb-1 md:flex-row md:space-x-2 md:mb-0"
                                    >
                                        <div class="w-full">
                                            <h3
                                                class="text-lg font-semibold text-gray-900"
                                            >
                                                {{ webinar.title }}
                                            </h3>
                                            <span
                                                v-if="webinar.registered"
                                                class="pb-1 text-xs text-green-800"
                                            >
                                                <i
                                                    class="fa-regular fa-user-check"
                                                ></i>
                                                Registered
                                            </span>
                                            <span
                                                v-if="!webinar.registered"
                                                class="pb-1 text-xs text-red-500"
                                            >
                                                Not registered yet
                                            </span>
                                        </div>
                                        <div
                                            class="flex justify-end mt-2 space-x-2 md:space-x-0 md:space-y-2 shrink-0 md:flex md:flex-col md:mt-0"
                                        >
                                            <button-success
                                                v-if="
                                                    webinar.webinar_url &&
                                                    webinar.registered
                                                "
                                                size="sm"
                                                @click="joinWebinar(webinar)"
                                            >
                                                <i
                                                    class="mr-1 fa-regular fa-video"
                                                ></i>
                                                Join events
                                            </button-success>

                                            <button-secondary
                                                v-if="webinar.page_url"
                                                size="sm"
                                                @click="
                                                    viewDetails(
                                                        webinar.page_url
                                                    )
                                                "
                                            >
                                                <i
                                                    class="mr-1 fa-regular fa-eye"
                                                ></i>
                                                Read more
                                            </button-secondary>

                                            <button-secondary
                                                v-if="!webinar.registered"
                                                size="sm"
                                                @click="register(webinar.id)"
                                            >
                                                <i
                                                    class="mr-1 fa-regular fa-user-plus"
                                                ></i>
                                                Register
                                            </button-secondary>

                                            <div
                                                class="flex items-center text-xs text-gray-700"
                                                v-if="
                                                    !webinar.webinar_url &&
                                                    webinar.registered
                                                "
                                            >
                                                <i
                                                    class="mr-1 fa-regular fa-hourglass-half"
                                                ></i>
                                                <span
                                                    >Join in
                                                    {{
                                                        timeUntilJoinable(
                                                            webinar.utc_start_time
                                                        )
                                                    }}</span
                                                >
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        class="grid flex-col grid-cols-3 gap-2 py-2 text-xs text-gray-600 lg:py-0 lg:flex md:flex-row lg:items-center lg:space-x-6 bg-gray-50 border-y fill-sidebar-padding md:border-y-0 md:bg-transparent"
                                    >
                                        <div
                                            class="flex items-center space-x-1"
                                        >
                                            <i
                                                class="fa-regular fa-calendar text-myflowPurple-200"
                                            ></i>
                                            <span>{{
                                                webinar.user_start_date
                                            }}</span>
                                        </div>
                                        <div
                                            class="flex items-center space-x-1"
                                        >
                                            <i
                                                class="fa-regular fa-clock text-myflowPurple-200"
                                            ></i>
                                            <span>{{
                                                webinar.user_start_time
                                            }}</span>
                                        </div>
                                        <div
                                            class="flex items-center space-x-1"
                                        >
                                            <i
                                                class="fa-regular fa-timer text-myflowPurple-200"
                                            ></i>
                                            <span>{{ webinar.duration }}</span>
                                        </div>
                                        <div
                                            class="flex items-center space-x-1"
                                            v-if="webinar.language"
                                        >
                                            <i
                                                class="fa-regular fa-globe text-myflowPurple-200"
                                            ></i>
                                            <span>{{
                                                webinar.language.toUpperCase()
                                            }}</span>
                                        </div>
                                        <div
                                            class="flex items-center col-span-2 space-x-1"
                                            v-if="webinar.language"
                                        >
                                            <i
                                                class="fa-regular fa-video text-myflowPurple-200"
                                            ></i>
                                            <span>Video meeting</span>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    class="mt-3"
                                    v-if="
                                        webinar.registered &&
                                        webinar.utc_start_time &&
                                        webinar.utc_end_time
                                    "
                                >
                                    <div
                                        class="p-4 border shadow-sm rounded-xl border-myflowPurple-900 bg-myflowPurple-50"
                                    >
                                        <div
                                            class="flex flex-col gap-1 mb-3 text-myflowPurple-900"
                                        >
                                            <p class="font-semibold">
                                                Add this to your calendar
                                            </p>
                                            <p class="text-sm text-myflowPurple-900">
                                                Make sure you don’t miss the live session.
                                            </p>
                                        </div>
                                        <div
                                            class="flex flex-wrap items-center gap-2"
                                        >
                                            <button-secondary
                                                size="sm"
                                                @click="
                                                    openGoogleCalendar(
                                                        webinar
                                                    )
                                                "
                                            >
                                                <i
                                                    class="mr-1 fa-brands fa-google"
                                                ></i>
                                                Google Calendar
                                            </button-secondary>
                                            <button-secondary
                                                size="sm"
                                                @click="
                                                    openOutlookCalendar(
                                                        webinar
                                                    )
                                                "
                                            >
                                                <i
                                                    class="mr-1 fa-brands fa-microsoft"
                                                ></i>
                                                Outlook
                                            </button-secondary>
                                            <button-secondary
                                                size="sm"
                                                @click="
                                                    downloadCalendarFile(
                                                        webinar
                                                    )
                                                "
                                            >
                                                <i
                                                    class="mr-1 fa-regular fa-calendar-plus"
                                                ></i>
                                                Apple / iCal
                                            </button-secondary>
                                        </div>
                                    </div>
                                </div>

                                <div class="mt-2">
                                    <p
                                        class="text-sm text-gray-700"
                                        v-if="webinar.description"
                                        v-html="
                                            webinar.description.replace(
                                                /\n/g,
                                                '<br>'
                                            )
                                        "
                                    ></p>
                                </div>
                            </div>
                        </div>
                    </list-basic-item>
                </list-basic>
            </template>
        </slideout-with-slots>
    </div>
</template>
